// Background auto-heal hook — probes server registration on route change,
// re-provisions if stale. Mounted in AdminLayout.

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/state/useAuth'
import { useTeam } from '@/state/useTeam'
import { getDefaultRelays } from '@/lib/baseUrl'
import { useServerProvisioningBanner } from '@/state/useServerProvisioningBanner'
import { runServerProvisioningAutoHeal } from '@/services/serverProvisioningAutoHeal'

export function useServerProvisioningAutoHeal(): void {
  const isAuthenticated = useAuth((s) => s.isAuthenticated)
  const teams = useTeam((s) => s.teams)
  const activeTeamId = useTeam((s) => s.activeTeamId)
  const restaurantPubkey = useTeam((s) => s.restaurantPubkey)
  const bunkerConnected = useTeam((s) => s.bunkerConnected)
  const signAsRestaurant = useTeam((s) => s.signAsRestaurant)
  const showBanner = useServerProvisioningBanner((s) => s.show)
  const hideBanner = useServerProvisioningBanner((s) => s.hide)

  const location = useLocation()

  const healthyTeamsRef = useRef<Set<number>>(new Set())
  const inFlightRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated) return
    if (activeTeamId === null) return
    if (!restaurantPubkey) return
    if (!bunkerConnected) return
    if (!Array.isArray(teams)) return

    const team = teams.find((t) => t.id === activeTeamId)
    if (!team) return
    const policyId = team.defaultPolicyId ?? 1

    if (healthyTeamsRef.current.has(activeTeamId)) return
    if (inFlightRef.current) return

    const currentTeamId = activeTeamId
    const relays = getDefaultRelays()

    inFlightRef.current = true
    void (async () => {
      const outcome = await runServerProvisioningAutoHeal({
        teamId: currentTeamId,
        restaurantPubkey,
        policyId,
        relays,
        signAsRestaurant,
      })
      if (outcome === 'ready' || outcome === 'healed') {
        healthyTeamsRef.current.add(currentTeamId)
        hideBanner()
      } else {
        showBanner()
      }
      inFlightRef.current = false
    })()
  }, [
    isAuthenticated,
    activeTeamId,
    restaurantPubkey,
    bunkerConnected,
    teams,
    signAsRestaurant,
    showBanner,
    hideBanner,
    location.pathname,
  ])
}
